import { useContext, useState, useMemo } from "react";
import { AuthContext, AuthContextProps } from "@/providers/AuthProvider";
import { useLobbyConfig } from "@/providers/LobbyConfigProvider";
import { useTranslation } from "react-i18next";
import { IShopFilters, ShopSortOptionEnum } from "./types";
import { useGetShopCategories, useGetShopItems, useGetUserBalance } from "./hooks/useShop";
import LoadingPage from "@/components/loadingPage/LoadingPage";
import ShopItems from "./components/ShopItems";
import ShopFilters from "./components/ShopFilters";
import BannerSlider from "@/components/bannerSlider/BannerSlider";
import ShopHistory from "./components/ShopHistory";
import Header from "./components/Header";
import BackToLobbyButton from "@/components/backToLobbyButton/BackToLobbyButton";

import styles from "./ShopWidget.module.scss";
import Popup from "@/components/popup/Popup";
import Button from "@/components/button/button";
import book from "@/assets/img/book.svg";
import TermsConditions from "./components/TermsConditions/TermsConditions";
import { useIsPromoMini } from "@/hooks/useIsPromoMini";
import { parseBannersConfig } from "@/widgets/casinoWidget/utils/bannerUtils";
import useViewportSize from "@/hooks/useViewportSize";

const ShopWidget = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<IShopFilters>({
    categoryId: null,
    tokenId: null,
    sortBy: ShopSortOptionEnum.CAN_PURCHASE,
    search: "",
    priceRange: null,
  });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const authContext = useContext<AuthContextProps>(AuthContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading: isShopItemsLoading,
    isFetchingNextPage,
  } = useGetShopItems(filters, {
    enabled: authContext?.isLoggedIn,
  });

  const { data: userBalanceData, isLoading: isUserBalanceLoading } = useGetUserBalance({
    enabled: authContext?.isLoggedIn,
  });

  const { data: shopCategoriesData, isLoading: isShopCategoriesLoading } = useGetShopCategories({
    enabled: authContext?.isLoggedIn,
  });

  const casinoHubConfig = useLobbyConfig();
  const translations = casinoHubConfig?.translations || [];
  const { i18n } = useTranslation();
  const { width } = useViewportSize();
  const banners = parseBannersConfig(casinoHubConfig.banner, i18n.language, width <= 768);
  const lng = i18n.language;
  const bannerTexts =
    translations.find((t: { lang: string }) => t.lang === lng) ?? translations[0] ?? null;

  const items = useMemo(() => {
    const allItems = data?.pages?.flatMap((page) => page?.data) ?? [];
    const filteredItems = filters.tokenId
      ? allItems.filter((item) => Number(item.token?.id) === filters.tokenId)
      : allItems;

    return filteredItems.filter(
      (item, index, items) => items.findIndex((currentItem) => currentItem.id === item.id) === index
    );
  }, [data, filters.tokenId]);

  const isMini = useIsPromoMini();

  if (!authContext?.isLoggedIn || isUserBalanceLoading || isShopCategoriesLoading) {
    return <LoadingPage />;
  }

  return (
    <div className={styles.background__img}>
      <BackToLobbyButton className={styles.backToHub} />
      {!isMini && <BannerSlider banners={banners} />}
      <div className={`${styles.container} ${isMini ? styles.miniContainer : ""}`}>
        <div className={`${!isMini ? styles.shopWidget : ""}`}>
          <Header
            onHistoryClick={() => setShowHistoryModal(true)}
            userBalance={Array.isArray(userBalanceData) ? userBalanceData : []}
            title={bannerTexts?.title}
            description={bannerTexts?.description}
          />

          {!isMini && (
            <ShopFilters
              filters={filters}
              setFilters={setFilters}
              categories={shopCategoriesData ?? []}
              tokens={
                Array.isArray(userBalanceData) ? userBalanceData.map((item) => item.token) : []
              }
            />
          )}

          <ShopItems
            items={items}
            userBalanceData={Array.isArray(userBalanceData) ? userBalanceData : []}
            isShopItemsLoading={isShopItemsLoading}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        </div>

        <ShopHistory isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)} />

        <div className={`${styles.termsConditions} ${isMini ? styles.miniTerms : ""}`}>
          <Button
            className={`${styles.rules} ${styles.a} ${isMini ? styles.miniRules : ""}`}
            label={t("userAuthPrompt.rules")}
            onClick={() => setIsModalOpen(true)}
            type="secondary"
            img={book}
          />
        </div>
      </div>

      <Popup isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className={styles.popup}>
        <TermsConditions config={casinoHubConfig.translations} />
      </Popup>
    </div>
  );
};

export default ShopWidget;
